/**
 * usePusher Hook
 * React hook for managing Pusher real-time subscriptions.
 *
 * Uses module-level ref-counting so that multiple `usePusher` instances sharing
 * the same channel won't tear each other's subscriptions down on unmount.
 */

import type { Channel } from 'pusher-js';
import { useCallback, useEffect, useRef } from 'react';
import { getPusherClient } from '../pusher';

/** Track total subscriptions per channel name across all usePusher instances. */
const channelRefCounts = new Map<string, number>();

export function usePusher() {
  const pusherRef = useRef(getPusherClient());
  const channelsRef = useRef<Map<string, Channel>>(new Map());

  const subscribe = useCallback((channelName: string): Channel => {
    const existingChannel = channelsRef.current.get(channelName);
    if (existingChannel) {
      return existingChannel;
    }

    channelRefCounts.set(channelName, (channelRefCounts.get(channelName) || 0) + 1);
    const channel = pusherRef.current.subscribe(channelName);
    channelsRef.current.set(channelName, channel);
    return channel;
  }, []);

  const unsubscribe = useCallback((channelName: string): void => {
    if (!channelsRef.current.has(channelName)) return;

    channelsRef.current.delete(channelName);
    _releaseRef(channelName);
  }, []);

  useEffect(() => {
    return () => {
      channelsRef.current.forEach((_, channelName) => {
        _releaseRef(channelName);
      });
      channelsRef.current.clear();
    };
  }, []);

  return { pusher: pusherRef.current, subscribe, unsubscribe };
}

function _releaseRef(channelName: string) {
  const count = (channelRefCounts.get(channelName) || 1) - 1;
  if (count <= 0) {
    channelRefCounts.delete(channelName);
    const pusher = getPusherClient();
    pusher.unsubscribe(channelName);
  } else {
    channelRefCounts.set(channelName, count);
  }
}
